// Workforce Framework module (tenant admin/HR/manager) — the horizontal-tab
// container, mirroring PerformancePage / DeveloperPage / the Finance hub.
//
// Phase 1 shipped the backend (workforce-framework-service). This shell adds the
// Evaluation Workspace tab (cycles → subjects → scoring → the explanation
// "show the why" surface). Further tabs (Framework Library/Builder, Reports)
// land in later phases — the tab list is set up to grow.
//
// Gated by workforce:framework:read; per-action perms are re-checked in the tab.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ClipboardList, FlaskConical, ShieldAlert, Store, TrendingUp, Workflow } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import { useHasPermission } from '@/rbac/useRbac';
import { EvaluationWorkspaceTab } from './EvaluationWorkspaceTab';
import { SimulationTab } from './SimulationTab';
import { SuccessionTab } from './SuccessionTab';
import { MarketplaceTab } from './MarketplaceTab';

const TAB_IDS = ['evaluations', 'simulation', 'succession', 'marketplace'] as const;
type TabId = (typeof TAB_IDS)[number];

export const WorkforcePage: React.FC = () => {
  const { t } = useTranslation('workforce');
  const canRead = useHasPermission('workforce:framework:read');

  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab') as TabId | null;
  const tab: TabId = requested && TAB_IDS.includes(requested) ? requested : 'evaluations';
  const setTab = (v: string) => setSearchParams({ tab: v }, { replace: true });

  if (!canRead) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">
          {t('no_access_title', { defaultValue: 'No access' })}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('no_access_body', {
            defaultValue: 'You do not have permission to view the Workforce framework.',
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal flex items-center gap-2">
          <Workflow className="w-6 h-6" /> {t('title', { defaultValue: 'Workforce' })}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('subtitle', {
            defaultValue:
              'Competency frameworks and evaluation cycles — score people against a published framework and see exactly how each grade was reached.',
          })}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="evaluations">
            <ClipboardList className="w-4 h-4 mr-1.5" />
            {t('tab_evaluations', { defaultValue: 'Evaluations' })}
          </TabsTrigger>
          <TabsTrigger value="simulation">
            <FlaskConical className="w-4 h-4 mr-1.5" />
            {t('tab_simulation', { defaultValue: 'Simulation' })}
          </TabsTrigger>
          <TabsTrigger value="succession">
            <TrendingUp className="w-4 h-4 mr-1.5" />
            {t('tab_succession', { defaultValue: 'Succession' })}
          </TabsTrigger>
          <TabsTrigger value="marketplace">
            <Store className="w-4 h-4 mr-1.5" />
            {t('tab_marketplace', { defaultValue: 'Marketplace' })}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="evaluations" className="mt-4">
          <EvaluationWorkspaceTab />
        </TabsContent>
        <TabsContent value="simulation" className="mt-4">
          <SimulationTab />
        </TabsContent>
        <TabsContent value="succession" className="mt-4">
          <SuccessionTab />
        </TabsContent>
        <TabsContent value="marketplace" className="mt-4">
          <MarketplaceTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WorkforcePage;
